import { WorkerRequirementStatus } from "@clipboard-health/contract-worker-app-bff";
import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";

import { LoadingButton } from "../../components/LoadingButton";
import { PageFooterWrapper } from "../../components/PageFooterWrapper";

interface DocumentUploadFooterProps {
  status: WorkerRequirementStatus;
  isLoading?: boolean;
  /**
   * Callback for the upload button.
   * Won't show if the status is IN_REVIEW.
   */
  onUploadButtonClick?: () => void;
  uploadButtonText?: string;
  replaceUploadButtonText?: string;
  /**
   * Callback for the sign button.
   * Won't show if the status is IN_REVIEW.
   */
  onSignButtonClick?: () => void;
  signButtonText?: string;
  replaceSignButtonText?: string;
  /**
   * Callback for the delete button.
   * Only shown if the status is IN_REVIEW.
   */
  onDeleteButtonClick?: () => void;
}

export function DocumentUploadFooter(props: DocumentUploadFooterProps) {
  const {
    status,
    onUploadButtonClick,
    onSignButtonClick,
    uploadButtonText,
    signButtonText,
    replaceSignButtonText,
    onDeleteButtonClick,
    isLoading = false,
    replaceUploadButtonText,
  } = props;

  if (status === WorkerRequirementStatus.IN_REVIEW) {
    return (
      <PageFooterWrapper>
        <LoadingButton
          fullWidth
          size="large"
          variant="outlined"
          isLoading={isLoading}
          onClick={onDeleteButtonClick}
        >
          Delete document
        </LoadingButton>
      </PageFooterWrapper>
    );
  }

  const buttonVariant = status === WorkerRequirementStatus.APPROVED ? "outlined" : "contained";
  const hasMultipleButtons = isDefined(onUploadButtonClick) && isDefined(onSignButtonClick);
  const isReplacement = [
    WorkerRequirementStatus.EXPIRING,
    WorkerRequirementStatus.APPROVED,
  ].includes(status);

  return (
    <PageFooterWrapper>
      <Stack spacing={4} sx={{ alignItems: "center" }}>
        {isDefined(onSignButtonClick) && (
          <LoadingButton
            fullWidth
            size="large"
            variant={buttonVariant}
            isLoading={isLoading}
            onClick={onSignButtonClick}
          >
            {isReplacement
              ? replaceSignButtonText ?? "Sign replacement document"
              : signButtonText ?? "Sign Document"}
          </LoadingButton>
        )}

        {hasMultipleButtons && (
          <Text variant="body1" sx={(theme) => ({ color: theme.palette.text.secondary })}>
            or
          </Text>
        )}

        {isDefined(onUploadButtonClick) && (
          <LoadingButton
            fullWidth
            size="large"
            variant={buttonVariant}
            isLoading={isLoading}
            onClick={onUploadButtonClick}
          >
            {isReplacement
              ? replaceUploadButtonText ?? "Upload replacement document"
              : uploadButtonText ?? "Upload Document"}
          </LoadingButton>
        )}
      </Stack>
    </PageFooterWrapper>
  );
}
